import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { Lesson, PendingLessonUpload } from '../types/lesson';
import type { Order } from '../types/order';
import { useUserPendingUploads } from '../hooks/usePendingUploads';
import { formatPrice } from '../utils/formatPrice';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { useStorageDownloadUrl } from '../hooks/useStorageDownloadUrl';
import { goToCoachProfile } from '../utils/coachNav';
import LessonStatusIcon from './LessonStatusIcon';
import { formatLessonDate, formatAbsoluteDate } from '../utils/lessonDate';

interface PlayerLessonCardProps {
  lesson: Lesson;
  order: Order;
  selected?: boolean;
  onClick: () => void;
}

// ── Shared helpers ────────────────────────────────────────────────────────────

/**
 * Primary line of a lesson row — the product, capitalised as a product name.
 * Replaces the coach name as the row's headline.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function lessonTitle(lesson: Lesson, order: Order): string {
  if (order.type === 'training_program') {
    return `Training Program · Lesson ${lesson.lessonNumber ?? '?'} of ${order.totalLessons ?? '?'}`;
  }
  if (order.type === 'shot_consult') return 'Shot Consult';
  return 'Single Lesson';
}

// eslint-disable-next-line react-refresh/only-export-components
export function lessonSubtitle(lesson: Lesson, order: Order): string {
  // Sentence-case lesson type labels with middle-dot separators (U+00B7)
  // — synced with the messages tab convention. "Training program",
  // "Shot consult", "Single async lesson" (no capital second word).
  if (order.type === 'training_program') {
    return `Training program · Lesson ${lesson.lessonNumber ?? ''} of ${order.totalLessons ?? '?'}`.trim();
  }
  if (order.type === 'shot_consult') return 'Shot consult';
  return 'Single async lesson';
}

/** Format order price for display. Returns null for free/zero-price orders. */
// eslint-disable-next-line react-refresh/only-export-components
export function displayPrice(_label?: string): string | null {
  return _label && !_label.toLowerCase().includes('beta') ? _label : null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function orderPriceDisplay(order: Order): string | null {
  const cents = order.priceCents ?? 0;
  if (cents === 0) return null;
  const total = order.type === 'training_program'
    ? cents * (order.totalLessons ?? 1)
    : cents;
  return formatPrice(total);
}

function getInitials(name?: string): string {
  return (name || 'C').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

/** Month + day, no year. Used for every row's status line, which is too
 *  tight at 380px to carry a full date. */
/** First frame of the player's submission, or null when the lesson has no
 *  thumbnail strip — reserved lessons have no video at all, and lessons
 *  uploaded before the preprocessor generated thumbnails have no strip. */
function firstFramePath(lesson?: Lesson | null): string | null {
  const base = lesson?.playerVideoThumbnailsBasePath;
  if (!base || (lesson?.playerVideoThumbnailCount ?? 0) === 0) return null;
  return `${base}/0.jpg`;
}

// ── Status descriptors ────────────────────────────────────────────────────────
// The label strings are unchanged from the pill era — only the presentation
// moved from a filled pill to a coloured dot on the row's third line.

interface Status {
  label: string;
  /** When true the dot slot renders the upload spinner instead of a dot. */
  spinner?: boolean;
}

// ── Row layout ────────────────────────────────────────────────────────────────
// One layout for every player-side lesson row. Reads as a list item, not a
// card: no border, radius or shadow of its own, and a hairline between rows.
// Selection chrome (the filled biscayne pill) is owned by the list in
// Lessons.tsx, which is the only surface with a concept of a selected row; the
// row itself only needs `selected` to invert its text.

interface LessonRowProps {
  coachId?: string;
  coachName?: string;
  coachAvatarUrl?: string | null;
  /** Storage path of the submission's first frame; null falls back to the coach. */
  thumbPath?: string | null;
  title: string;
  status: Status;
  /** Trailing meta after the status — usually a date. */
  meta?: string;
  testId?: string;
  /** Set by the list when this row is the open one. */
  selected?: boolean;
  onClick: () => void;
}

function LessonRow({
  coachId,
  coachName,
  coachAvatarUrl,
  thumbPath,
  title,
  status,
  meta,
  testId,
  selected = false,
  onClick,
}: LessonRowProps) {
  const { currentUser } = useAuth();
  const initials = getInitials(coachName);

  const thumbUrl = useStorageDownloadUrl(thumbPath ?? null);
  const [thumbBroken, setThumbBroken] = useState(false);
  // The thumbnail leads when we have one; otherwise the coach avatar keeps the
  // slot exactly as it renders today. Never a grey placeholder: reserved
  // lessons have no video, and older lessons predate thumbnail generation.
  const showThumb = !!thumbUrl && !thumbBroken;

  const coachCircle = (size: number, textSize: string) => (
    <div
      className={`rounded-full flex items-center justify-center overflow-hidden ${textSize} font-bold text-white flex-shrink-0`}
      style={{ background: '#54C3EA', width: size, height: size }}
    >
      {coachAvatarUrl
        ? <img src={coachAvatarUrl} alt={coachName ?? 'Coach'} className="w-full h-full object-cover" />
        : initials}
    </div>
  );

  // The CMS profile is the canonical coach page app-wide (Discover, Messages,
  // RequestLesson all go there). stopPropagation keeps the click off the row.
  //
  // `expandHit` grows the tap target to 44px via a transparent ::after at
  // -12px on every side. Chosen over padding + negative margin because it
  // costs the flex row no layout at all — the circle stays exactly 20px and
  // the coach name beside it does not move. Only the 20px variant needs it;
  // the 48px fallback already clears the minimum.
  const coachLink = (node: React.ReactNode, expandHit = false) => (coachId ? (
    <button
      type="button"
      title={coachName ?? 'Coach'}
      onClick={(e) => { e.stopPropagation(); goToCoachProfile(coachId, currentUser?.uid ?? undefined); }}
      className={`flex-shrink-0 border-none bg-transparent p-0 cursor-pointer${
        expandHit ? " relative after:absolute after:content-[''] after:-inset-3" : ''
      }`}
    >
      {node}
    </button>
  ) : node);

  const leading = showThumb ? (
    // Not a link — the thumbnail selects the row like the rest of it.
    <img
      src={thumbUrl!}
      alt=""
      onError={() => setThumbBroken(true)}
      className="w-11 h-11 object-cover flex-shrink-0 bg-black"
      style={{ borderRadius: 8 }}
    />
  ) : coachLink(coachCircle(44, 'text-[15px]'));

  // Secondary text on a filled biscayne row, matching the Messages sidebar.
  const secondary = selected ? 'text-white/80' : 'text-[rgba(60,60,67,0.6)]';

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testId}
      data-lesson-row
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`ios-press cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ${
        selected ? '' : 'border-b border-b-[#e6e6e6]'
      }`}
      style={{ padding: '12px 16px' }}
    >
      <div className="flex items-start gap-3">
        {leading}

        <div className="flex-1 min-w-0">
          {/* Subhead 15/20. */}
          <div className={`text-[15px] leading-[20px] font-semibold truncate ${selected ? 'text-white' : 'text-[#1C1C1C]'}`}>
            {title}
          </div>
          {/* Coach identity rides line 2 once the thumbnail owns the leading
              slot. When it doesn't, the 44px avatar above is already the link,
              so a second copy here would just repeat it. Footnote 13/18. */}
          <div className={`flex items-center text-[13px] leading-[18px] min-w-0 ${secondary}`} style={{ gap: 6 }}>
            {showThumb && coachLink(coachCircle(16, 'text-[8px]'), true)}
            <span className="truncate">{coachName}</span>
          </div>
          <div className={`flex flex-wrap items-center gap-x-[6px] mt-[2px] text-[13px] leading-[18px] min-w-0 ${secondary}`}>
            {status.spinner ? (
              // Kept as a spinner: an icon would lose the fact that something
              // is actively moving.
              <span className={`w-3 h-3 border-[1.5px] rounded-full animate-spin inline-block flex-shrink-0 ${
                selected ? 'border-white/80 border-t-transparent' : 'border-[#1A6B8A] border-t-transparent'
              }`} />
            ) : (
              <LessonStatusIcon label={status.label} selected={selected} />
            )}
            {/* Nothing truncates because the screen is narrow, and
                nothing is dropped either. It reflows: below md the date takes
                its own line under the status. */}
            <span className="shrink-0">{status.label}</span>
            {meta && <span className="hidden md:inline truncate shrink-0">· {meta}</span>}
            {meta && <span className="md:hidden truncate basis-full pl-5">{meta}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Training-program "awaiting upload" card (no lesson doc exists yet) ───────

interface TrainingProgramAwaitingCardProps {
  order: Order;
  nextLessonNum: number;
  lastDeliveredAt?: string;
  selected?: boolean;
  onClick: () => void;
}

export function TrainingProgramAwaitingCard({
  order,
  nextLessonNum,
  lastDeliveredAt,
  onClick,
  selected,
}: TrainingProgramAwaitingCardProps) {
  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachName = liveCoach?.name ?? order.coachName;
  const avatarUrl = liveCoach?.avatarUrl ?? order.coachAvatarUrl;

  return (
    <LessonRow
      selected={selected}
      testId="bundle-awaiting-upload-card"
      coachId={order.coachId}
      coachName={coachName}
      coachAvatarUrl={avatarUrl}
      title={`Training Program, Lesson ${nextLessonNum} of ${order.totalLessons ?? '?'}`}
      status={{ label: 'Ready to submit' }}
      meta={lastDeliveredAt ? `Lesson ${nextLessonNum - 1} delivered ${formatLessonDate(lastDeliveredAt)}` : undefined}
      onClick={onClick}
    />
  );
}

// ── Completed training program bundle card ──────────────────────────────────

interface CompletedBundleCardProps {
  order: Order;
  lessons: Lesson[];
  selected?: boolean;
  onClick: () => void;
}

export function CompletedBundleCard({ order, lessons, onClick, selected }: CompletedBundleCardProps) {
  const totalLessons = order.totalLessons ?? lessons.length;
  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachName = liveCoach?.name ?? order.coachName;
  const coachAvatarUrl = liveCoach?.avatarUrl ?? order.coachAvatarUrl;

  const sortedLessons = [...lessons].sort((a, b) => (a.lessonNumber ?? 0) - (b.lessonNumber ?? 0));
  // Absolute, not relative: this pair forms a span, and "Aug 1 – Today" mixes
  // two frames of reference.
  const startDate = order.createdAt ? formatAbsoluteDate(order.createdAt) : '';
  const lastDelivered = sortedLessons[sortedLessons.length - 1]?.deliveredAt;
  const endDate = lastDelivered ? formatAbsoluteDate(lastDelivered) : '';
  const dateRange = startDate && endDate ? `${startDate} – ${endDate}` : startDate;
  const price = orderPriceDisplay(order);

  return (
    <LessonRow
      selected={selected}
      coachId={order.coachId}
      coachName={coachName}
      coachAvatarUrl={coachAvatarUrl}
      thumbPath={firstFramePath(sortedLessons[sortedLessons.length - 1])}
      title={`Training Program, ${totalLessons} of ${totalLessons}`}
      status={{ label: 'Complete' }}
      meta={`${dateRange}${price ? ` · ${price}` : ''}`}
      onClick={onClick}
    />
  );
}

// ── Delivered / completed rows on the Delivered tab ──────────────────────────

interface PlayerDeliveredLessonRowProps {
  lesson: Lesson;
  order: Order;
  selected?: boolean;
  onClick: () => void;
}

export function PlayerDeliveredLessonRow({ lesson, order, onClick, selected }: PlayerDeliveredLessonRowProps) {
  // Use the lesson's coachId (transfer-aware) if present; otherwise the order's.
  const effectiveCoachId = lesson.coachId ?? order.coachId;
  const liveCoach = useUserIdentity(effectiveCoachId, 'coach');
  const coachName = liveCoach?.name ?? lesson.coachName ?? order.coachName ?? 'Coach';
  const coachAvatarUrl = liveCoach?.avatarUrl ?? lesson.coachAvatarUrl ?? order.coachAvatarUrl;
  const deliveredAt = lesson.deliveredAt;
  const deliveredLabel = deliveredAt ? `Delivered ${formatLessonDate(deliveredAt)}` : 'Delivered';
  // Pill only when the lesson is unwatched — same rule, now a dot.
  const isUnwatched = !lesson.playerViewedAt;

  return (
    <LessonRow
      selected={selected}
      coachId={effectiveCoachId}
      coachName={coachName}
      coachAvatarUrl={coachAvatarUrl}
      thumbPath={firstFramePath(lesson)}
      title={lessonTitle(lesson, order)}
      status={{ label: isUnwatched ? 'Ready to watch' : 'Complete' }}
      meta={deliveredLabel}
      onClick={onClick}
    />
  );
}

interface PlayerCompletedBundleRowProps {
  order: Order;
  lessons: Lesson[];
  selected?: boolean;
  onClick: () => void;
}

export function PlayerCompletedBundleRow({ order, lessons, onClick, selected }: PlayerCompletedBundleRowProps) {
  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachName = liveCoach?.name ?? order.coachName ?? 'Coach';
  const coachAvatarUrl = liveCoach?.avatarUrl ?? order.coachAvatarUrl;
  const totalLessons = order.totalLessons ?? lessons.length;
  const sorted = [...lessons].sort((a, b) => (a.lessonNumber ?? 0) - (b.lessonNumber ?? 0));
  const lastDelivered = sorted[sorted.length - 1]?.deliveredAt ?? order.completedAt;

  return (
    <LessonRow
      selected={selected}
      coachId={order.coachId}
      coachName={coachName}
      coachAvatarUrl={coachAvatarUrl}
      thumbPath={firstFramePath(sorted[sorted.length - 1])}
      title={`Training Program, ${totalLessons} of ${totalLessons}`}
      status={{ label: 'Complete' }}
      meta={lastDelivered ? `Delivered ${formatLessonDate(lastDelivered)}` : undefined}
      onClick={onClick}
    />
  );
}

// ── Main exported component ──────────────────────────────────────────────────

/**
 * The row for a booking whose upload has finished but whose lesson does not
 * exist yet — the transcode window, which runs about half a minute.
 *
 * Uses the same LessonRow as every other player row rather than a lookalike,
 * so the pending row cannot drift away from the real one it turns into. That
 * is possible because LessonRow takes flat props rather than a Lesson and an
 * Order; nothing here fabricates a half-built domain object.
 *
 * What is real: the title, the coach, and the status. All three come off the
 * upload doc, which carries offeringTitle and coachId from the moment the
 * player pressed submit.
 *
 * What is not: the thumbnail. It is the first frame of the processed video and
 * that video does not exist yet. Passing thumbPath={null} takes LessonRow's
 * existing fallback — the coach avatar in the same slot, which is what a
 * reserved lesson and any pre-thumbnail lesson already show. Deliberately not
 * a grey placeholder: the slot stays occupied by something meaningful, so
 * nothing moves or flashes when the real thumbnail arrives.
 */
export function PendingUploadRow({
  upload,
  selected,
  onClick,
}: {
  upload: PendingLessonUpload;
  selected?: boolean;
  onClick: () => void;
}) {
  const coach = useUserIdentity(upload.coachId, 'coach');
  return (
    <LessonRow
      selected={selected}
      testId="pending-upload-row"
      coachId={upload.coachId}
      coachName={coach?.name ?? undefined}
      coachAvatarUrl={coach?.avatarUrl}
      thumbPath={null}
      title={upload.offeringTitle}
      /* "Uploaded", matching the detail pane word for word. The spinner marks
         it as still in flight — the file has landed, the lesson has not. */
      status={{ label: 'Uploaded', spinner: true }}
      onClick={onClick}
    />
  );
}

export default function PlayerLessonCard({ lesson, order, onClick, selected }: PlayerLessonCardProps) {
  const effectiveCoachId = lesson.coachId ?? order.coachId;
  const liveCoach = useUserIdentity(effectiveCoachId, 'coach');
  const coachName = liveCoach?.name ?? lesson.coachName ?? order.coachName;
  const avatarUrl = liveCoach?.avatarUrl ?? lesson.coachAvatarUrl ?? order.coachAvatarUrl;
  const isActive = lesson.status === 'reserved' || lesson.status === 'in_progress';
  const { uploads: pendingUploads } = useUserPendingUploads();
  const activePendingUpload = pendingUploads.find(
    u => (u.existingLessonId === lesson.id || u.lessonId === lesson.id) &&
         (u.status === 'uploading' || u.status === 'uploaded' || u.status === 'processing')
  );
  // If playerVideoUrl is already set but the CF hasn't flipped the Firestore status yet,
  // treat the video as submitted so we don't show stale "Submit Video" / upload UX.
  const videoSubmitted = !!lesson.playerVideoUrl;

  if (isActive) {
    // Bare date: the status label already says what happened, so the leading
    // verb ("Submitted" / "Created") was repeating it and costing the room the
    // date needed.
    /* Always a date. It used to carry "Your video is being uploaded…" whenever
       an upload existed, without checking which phase it was in — so once the
       file had landed the status said "Processing" and this still said
       "uploading". Two halves of one row disagreeing about one state. The
       status line owns the state; this owns the date. */
    const meta = (lesson.status === 'in_progress' || videoSubmitted)
      ? formatLessonDate(lesson.updatedAt ?? lesson.createdAt)
      : formatLessonDate(lesson.createdAt);

    // Status-only vocabulary: no imperatives. "Ready to submit" describes the
    // player's queued-up state; "Awaiting analysis" describes the
    // submitted state. Strings unchanged from the pill era.
    const status: Status = lesson.status === 'reserved' && activePendingUpload && !videoSubmitted
      ? {
          /* "Uploaded", not "Processing", and the same word the modal and the
             detail pane use for this state. It tells the player the part that
             could fail is done and their tab is free; "Processing" named our
             job rather than their outcome. The spinner carries "still
             working". */
          label: activePendingUpload.status === 'uploading'
            ? `${activePendingUpload.uploadProgress ?? 0}%`
            : 'Uploaded',
          spinner: true,
        }
      : lesson.status === 'reserved' && !videoSubmitted
        ? { label: 'Ready to submit' }
        : { label: 'Awaiting analysis' };

    return (
      <LessonRow
        selected={selected}
        testId={lesson.status === 'reserved' && !videoSubmitted ? 'reserved-lesson-pill' : undefined}
        coachId={effectiveCoachId}
        coachName={coachName}
        coachAvatarUrl={avatarUrl}
        thumbPath={firstFramePath(lesson)}
        title={lessonTitle(lesson, order)}
        status={status}
        meta={meta}
        onClick={onClick}
      />
    );
  }

  // Delivered (and any other terminal state — currently only 'delivered'
  // ever reaches this branch).
  return (
    <LessonRow
      selected={selected}
      coachId={effectiveCoachId}
      coachName={coachName}
      coachAvatarUrl={avatarUrl}
      thumbPath={firstFramePath(lesson)}
      title={lessonTitle(lesson, order)}
      status={{ label: 'Ready to watch' }}
      meta={lesson.deliveredAt ? `Delivered ${formatLessonDate(lesson.deliveredAt)}` : undefined}
      onClick={onClick}
    />
  );
}
